import { useEffect, useState } from 'react'
import { api } from '../../api/endpoints'
import { errorMessage } from '../../api/client'
import type { User } from '../../api/types'
import { db } from '../../db/db'

/** Someone who can be picked: an active person, or a bot where bots are allowed. A managing system's actor never is. */
export function isPickable(user: User, includeBots: boolean): boolean {
  if (user.kind === 'bot') return includeBots
  return user.isActive && user.kind === 'person'
}

export interface DirectorySearch {
  /** Pickable people (and bots, where allowed) for the query, in the directory's order. */
  results: User[]
  status: 'idle' | 'loading' | 'error'
  error: string
  /** The server could not be reached: these are the people already known on this phone. */
  offline: boolean
}

/** Staff directory search (GET /api/users?q=), falling back to the people this device knows when offline. */
export function useDirectorySearch(query: string, includeBots: boolean, limit = 20): DirectorySearch {
  const [state, setState] = useState<DirectorySearch>({ results: [], status: 'loading', error: '', offline: false })

  useEffect(() => {
    let cancelled = false
    setState((s) => ({ ...s, status: 'loading' }))
    api
      .searchUsers(query, limit)
      .then(async (users) => {
        if (cancelled) return
        await db.users.bulkPut(users)
        if (!cancelled) setState({ results: users.filter((u) => isPickable(u, includeBots)), status: 'idle', error: '', offline: false })
      })
      .catch(async (e) => {
        if (cancelled) return
        // Offline: people already known on this device (not bots: only the server knows which are listed).
        const q = query.toLowerCase()
        const local = await db.users
          .filter((u) => isPickable(u, false) && (!q || u.displayName.toLowerCase().includes(q) || u.username.toLowerCase().includes(q)))
          .limit(limit)
          .toArray()
        if (!cancelled) setState({ results: local, status: local.length ? 'idle' : 'error', error: errorMessage(e), offline: true })
      })
    return () => {
      cancelled = true
    }
  }, [query, includeBots, limit])

  return state
}
